import EmberObject from '@ember/object';
import Service, { service } from '@ember/service';

import { pluralize } from '@warp-drive/utilities/string';
import { TrackedArray, TrackedMap } from 'tracked-built-ins';

import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export class RouteItem {
  // Defaults
  formModels = new TrackedArray([]);
  routeData = new TrackedMap();
  model;
  queryParams = {};

  // Hooks
  constructor(
    model,
    isExplorerRoute,
    options = {},
    recordPresentation,
    recordLocks,
    recordRelationships,
  ) {
    this.model = model;
    this.isExplorerRoute = isExplorerRoute;
    this.options = options;
    this.queryParams = options.queryParams ?? {};
    this.recordPresentation = recordPresentation;
    this.recordLocks = recordLocks;
    this.recordRelationships = recordRelationships;
  }

  // Getter and setter
  get hasDirtyModel() {
    let formModels = this.formModels.filter((model) => !model.isDeleted);

    return formModels.some((model) =>
      this.recordRelationships.hasDirtyFields(model),
    );
  }

  get recordType() {
    return getRecordType(this.model);
  }

  get name() {
    return this.recordPresentation.recordHeadlineOrNew(this.model);
  }

  get routeName() {
    let segments = ['admin', pluralize(this.recordType)];

    if (!this.isExplorerRoute) {
      segments.push(this.model.get('isNew') ? 'new' : this.recordType);
    } else {
      segments.push('index');
    }

    return segments.join('.');
  }

  get url() {
    let segments = [pluralize(this.recordType)];

    if (!this.isExplorerRoute) {
      segments.push(this.model.get('isNew') ? 'new' : this.model.get('id'));
    }

    return `/${segments.join('/')}`;
  }

  get transitionToParams() {
    let params = [this.routeName];

    if (!this.model.get('isNew') && !this.isExplorerRoute) {
      params.push(this.model);
    }

    params.push({ queryParams: this.queryParams });

    return params;
  }

  // Functions
  findOrRegisterFormModel(model) {
    let formModel = this.formModels.find((formModel) => formModel === model);

    if (formModel) {
      return formModel;
    } else {
      return this._registerFormModel(model);
    }
  }

  async unlockAllForms() {
    const unlockPromises = this.formModels
      .filter((model) => this.recordLocks.isLockable(model))
      .map((model) => this.recordLocks.unlock(model));

    return await Promise.all(unlockPromises);
  }

  _registerFormModel(model) {
    this.formModels.push(model);

    if (this.recordLocks.isLockable(model)) {
      this.recordLocks.lock(model);
    }

    return model;
  }
}

export default class BreadcrumbService extends Service {
  // Services
  @service confirm;
  @service intl;
  @service recordLocks;
  @service recordRelationships;
  @service recordPresentation;
  @service router;
  @service store;

  // Defaults
  routes = new TrackedArray([]);
  transitionAfterRollback = null;

  // Getter and setter
  get currentRoute() {
    return this.routes[this.routes.length - 1];
  }

  get firstRoute() {
    return this.routes[0];
  }

  get hasRoutes() {
    return this.routes.length > 0;
  }

  get isBreadcrumbVisible() {
    return this.routes.length > 1;
  }

  get prevRoute() {
    return this.routes[this.routes.length - 2];
  }

  // Actions
  /*
  This action is called on the routeWillChange transition inside the admin route to check if the user tries to rollback any route items.
  */
  checkRoutesToRollback = async (transition) => {
    if (
      !this.hasRoutes ||
      this._isTransitionWhitelisted(transition) ||
      this._isTransitionToCurrentRoute(transition)
    ) {
      return;
    }

    return this._rollbackCurrentRoute(transition);
  };

  /*
  This action is called on the routeDidChange transition inside the admin route to check if a transition after a rollback is defined.
  */
  checkTransitionAfterRollback = (transition) => {
    if (
      !this.transitionAfterRollback ||
      this._isTransitionWhitelisted(transition)
    ) {
      return;
    }

    this.transitionAfterRollback.retry();
    this.transitionAfterRollback = null;
  };

  // Functions
  /*
  This function is used by new routes to load existing models instead of creating a new one, if the user returns to a route which is registered.
  */
  findRouteModelOrCreateRecord(recordType) {
    const routeItem = this._findRoute(`/${pluralize(recordType)}/new`);

    if (routeItem) {
      return routeItem?.model;
    } else {
      return this.store.createRecord(recordType);
    }
  }

  /*
  This function is used by the record explorer table and ui form relations
  components to register the attempted route item before transition to it.
  */
  registerDetailRoute(model, options) {
    let route = this._findRoute(this._getModelUrl(model));

    if (route) {
      return route;
    }

    if (!this.routes.length) {
      const recordType =
        options?.recordExplorerModelName ?? getRecordType(model);
      this.registerExplorerRoute(recordType);
    }

    route = new RouteItem(
      model,
      false,
      options,
      this.recordPresentation,
      this.recordLocks,
      this.recordRelationships,
    );
    this.routes.push(route);

    return route;
  }

  registerExplorerRoute(recordType, options) {
    if (this._findRoute(`/${pluralize(recordType)}`)) {
      return;
    }
    const route = new RouteItem(
      this._createExplorerRoute(recordType),
      true,
      options,
      this.recordPresentation,
      this.recordLocks,
      this.recordRelationships,
    );
    this.routes.push(route);

    return route;
  }

  // Privat functions
  _createExplorerRoute(recordType) {
    return EmberObject.create({
      recordType,
      displayHeadline: this.intl.t(`models.plurals.${recordType}`),
    });
  }

  _findRoute(url) {
    return this.routes.find((route) => route.url === url);
  }

  _isTransitionToCurrentRoute(transition) {
    return this._getTransitionUrl(transition) === this.currentRoute?.url;
  }

  _getTransitionUrl(transition) {
    let url = '';
    let intent = transition.intent;
    let paramNames = transition.to?.paramNames;
    let contexts =
      intent?.contexts ??
      paramNames?.map((paramName) => {
        return transition.to?.params[paramName];
      });

    switch (contexts.length) {
      case 0:
        url = this.router.urlFor(transition.to?.name);
        break;
      case 1:
        url = this.router.urlFor(transition.to?.name, contexts[0]);
        break;
      default:
        url = this.router.urlFor(transition.to?.name, contexts);
        break;
    }

    return url;
  }

  _getModelUrl(model) {
    const recordType = getRecordType(model);
    const isNew = model.get('isNew');
    const id = model.get('id');

    return `/${pluralize(recordType)}/${isNew ? 'new' : id}`;
  }

  _isTransitionWhitelisted(transition) {
    let isLoading = transition.to?.name?.split('.').includes('loading');

    /*
    1. If transition is aborted we don't need to check again (infinite loop)
    2. The loading routes are ignored by the breadcrumb
    */
    return transition.isAborted || isLoading;
  }

  async _rollbackCurrentRoute(transition) {
    let prevRoute = this.prevRoute;

    transition.abort();

    if (this.currentRoute.hasDirtyModel) {
      let confirmed = await this.confirm.ask(
        'application.confirm.rollback.title',
        'application.confirm.rollback.message',
        {
          componentName: 'rollback',
          currentRoute: this.currentRoute,
        },
      );

      if (!confirmed) {
        return;
      }

      if (this.currentRoute.options.beforeRollback) {
        await this.currentRoute.options.beforeRollback();
      }

      this.currentRoute.formModels.forEach((item) => {
        this.recordRelationships.rollback(item);
        item.rollbackAttributes();
      });
    }

    await this.currentRoute.unlockAllForms();
    this.routes.pop();

    if (prevRoute && !prevRoute.isExplorerRoute) {
      this.transitionAfterRollback = transition;

      this.router.transitionTo(...prevRoute.transitionToParams);
    } else {
      // Pop routes again to remove base route, if final transition is not base route
      if (this._getTransitionUrl(transition) !== prevRoute?.url) {
        this.routes.pop();
      }

      if (transition.isAborted) {
        transition.retry();
      }
    }

    return;
  }
}
